import ApiClient from './apiClient';
import type {
  Operador,
  Faena,
  Predio,
  Rodal,
  Insumo,
  ItemChequeo,
  Proveedor,
  Vehiculo,
  Tarjeta,
  Emisor,
  PaginatedResponse
} from '../types';

// ===============================
// INTERFACES DE FILTROS
// ===============================

interface BaseFilter {
  page?: number;
  limit?: number;
  search?: string;
  estado?: 'activo' | 'inactivo';
}

interface FaenaFilter extends BaseFilter {
  ubicacion?: string;
}

interface PredioFilter extends BaseFilter {
  superficie_min?: number;
  superficie_max?: number;
}

interface RodalFilter extends BaseFilter {
  especie?: string;
  predio_id?: number;
}

interface InsumoFilter extends BaseFilter {
  unidad_medida?: string;
  categoria?: string;
}

interface ItemChequeoFilter extends BaseFilter {
  categoria?: string;
}

interface ProveedorFilter extends BaseFilter {}

interface VehiculoFilter extends BaseFilter {
  tipo?: string;
}

// ===============================
// INTERFACES DE CREACIÓN
// ===============================

interface CrearOperador {
  codigo: string;
  descripcion: string;
  nombre_completo?: string;
  telefono?: string;
  email?: string;
  estado?: 'activo' | 'inactivo';
}

interface CrearFaena {
  codigo: string;
  descripcion: string;
  ubicacion?: string;
  estado?: 'activo' | 'inactivo';
}

interface CrearPredio {
  codigo: string;
  descripcion: string;
  superficie?: number;
  estado?: 'activo' | 'inactivo';
}

interface CrearRodal {
  codigo: string;
  descripcion: string;
  especie?: string;
  predio_id?: number;
  estado?: 'activo' | 'inactivo';
}

interface CrearInsumo {
  codigo: string;
  descripcion: string;
  unidad_medida?: string;
  categoria?: string;
}

interface CrearItemChequeo {
  codigo: string;
  descripcion: string;
  categoria?: string;
}

interface CrearProveedor {
  codigo: string;
  nombre: string;
  rut?: string;
  direccion?: string;
  email?: string;
  estado?: 'activo' | 'inactivo';
}

interface CrearVehiculo {
  codigo: string;
  patente: string;
  tipo?: string;
  marca?: string;
  modelo?: string;
  estado?: 'activo' | 'inactivo';
}

interface TarjetaFilter extends BaseFilter {}
interface EmisorFilter extends BaseFilter {}

interface CrearTarjeta {
  compania?: string;
  numero: string;
  descripcion?: string;
  estado?: 'activo' | 'inactivo';
}

interface CrearEmisor {
  nombre: string;
  cargo?: string;
  estado?: 'activo' | 'inactivo';
}

// ===============================
// SERVICIO DE REFERENCIAS
// ===============================

class ReferenciasService {

  // ===============================
  // OPERADORES
  // ===============================

  async obtenerOperadores(filtros: BaseFilter = {}): Promise<PaginatedResponse<Operador>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);

    const response = await ApiClient.get(`/operadores?${params.toString()}`);
    return response.data;
  }

  async obtenerOperador(id: number): Promise<Operador> {
    const response = await ApiClient.get(`/operadores/${id}`);
    return response.data;
  }

  async crearOperador(datos: CrearOperador): Promise<Operador> {
    const response = await ApiClient.post(`/operadores`, datos);
    return response.data;
  }

  async actualizarOperador(id: number, datos: Partial<CrearOperador>): Promise<Operador> {
    const response = await ApiClient.put(`/operadores/${id}`, datos);
    return response.data;
  }

  async eliminarOperador(id: number): Promise<void> {
    await ApiClient.delete(`/operadores/${id}`);
  }

  // ===============================
  // FAENAS
  // ===============================

  async obtenerFaenas(filtros: FaenaFilter = {}): Promise<PaginatedResponse<Faena>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.ubicacion) params.append('ubicacion', filtros.ubicacion);

    const response = await ApiClient.get(`/faenas?${params.toString()}`);
    return response.data;
  }

  async obtenerFaena(id: number): Promise<Faena> {
    const response = await ApiClient.get(`/faenas/${id}`);
    return response.data;
  }

  async crearFaena(datos: CrearFaena): Promise<Faena> {
    const response = await ApiClient.post(`/faenas`, datos);
    return response.data;
  }

  async actualizarFaena(id: number, datos: Partial<CrearFaena>): Promise<Faena> {
    const response = await ApiClient.put(`/faenas/${id}`, datos);
    return response.data;
  }

  async eliminarFaena(id: number): Promise<void> {
    await ApiClient.delete(`/faenas/${id}`);
  }

  // ===============================
  // PREDIOS
  // ===============================

  async obtenerPredios(filtros: PredioFilter = {}): Promise<PaginatedResponse<Predio>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.superficie_min) params.append('superficie_min', filtros.superficie_min.toString());
    if (filtros.superficie_max) params.append('superficie_max', filtros.superficie_max.toString());

    const response = await ApiClient.get(`/predios?${params.toString()}`);
    return response.data;
  }

  async obtenerPredio(id: number): Promise<Predio> {
    const response = await ApiClient.get(`/predios/${id}`);
    return response.data;
  }

  async crearPredio(datos: CrearPredio): Promise<Predio> {
    const response = await ApiClient.post(`/predios`, datos);
    return response.data;
  }

  async actualizarPredio(id: number, datos: Partial<CrearPredio>): Promise<Predio> {
    const response = await ApiClient.put(`/predios/${id}`, datos);
    return response.data;
  }

  async eliminarPredio(id: number): Promise<void> {
    await ApiClient.delete(`/predios/${id}`);
  }

  // ===============================
  // RODALES
  // ===============================

  async obtenerRodales(filtros: RodalFilter = {}): Promise<PaginatedResponse<Rodal>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.especie) params.append('especie', filtros.especie);
    if (filtros.predio_id) params.append('predio_id', filtros.predio_id.toString());

    const response = await ApiClient.get(`/rodales?${params.toString()}`);
    return response.data;
  }

  async obtenerRodal(id: number): Promise<Rodal> {
    const response = await ApiClient.get(`/rodales/${id}`);
    return response.data;
  }

  async crearRodal(datos: CrearRodal): Promise<Rodal> {
    const response = await ApiClient.post(`/rodales`, datos);
    return response.data;
  }

  async actualizarRodal(id: number, datos: Partial<CrearRodal>): Promise<Rodal> {
    const response = await ApiClient.put(`/rodales/${id}`, datos);
    return response.data;
  }

  async eliminarRodal(id: number): Promise<void> {
    await ApiClient.delete(`/rodales/${id}`);
  }

  // ===============================
  // INSUMOS
  // ===============================

  async obtenerInsumos(filtros: InsumoFilter = {}): Promise<PaginatedResponse<Insumo>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.unidad_medida) params.append('unidad_medida', filtros.unidad_medida);
    if (filtros.categoria) params.append('categoria', filtros.categoria);

    const response = await ApiClient.get(`/insumos?${params.toString()}`);
    return response.data;
  }

  async obtenerInsumo(id: number): Promise<Insumo> {
    const response = await ApiClient.get(`/insumos/${id}`);
    return response.data;
  }

  async crearInsumo(datos: CrearInsumo): Promise<Insumo> {
    const response = await ApiClient.post(`/insumos`, datos);
    return response.data;
  }

  async actualizarInsumo(id: number, datos: Partial<CrearInsumo>): Promise<Insumo> {
    const response = await ApiClient.put(`/insumos/${id}`, datos);
    return response.data;
  }

  async eliminarInsumo(id: number): Promise<void> {
    await ApiClient.delete(`/insumos/${id}`);
  }

  // ===============================
  // ITEMS DE CHEQUEO
  // ===============================

  async obtenerItemsChequeo(filtros: ItemChequeoFilter = {}): Promise<PaginatedResponse<ItemChequeo>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.categoria) params.append('categoria', filtros.categoria);

    const response = await ApiClient.get(`/items-chequeo?${params.toString()}`);
    return response.data;
  }

  async obtenerItemChequeo(id: number): Promise<ItemChequeo> {
    const response = await ApiClient.get(`/items-chequeo/${id}`);
    return response.data;
  }

  async crearItemChequeo(datos: CrearItemChequeo): Promise<ItemChequeo> {
    const response = await ApiClient.post(`/items-chequeo`, datos);
    return response.data;
  }

  async actualizarItemChequeo(id: number, datos: Partial<CrearItemChequeo>): Promise<ItemChequeo> {
    const response = await ApiClient.put(`/items-chequeo/${id}`, datos);
    return response.data;
  }

  async eliminarItemChequeo(id: number): Promise<void> {
    await ApiClient.delete(`/items-chequeo/${id}`);
  }

  // ===============================
  // PROVEEDORES
  // ===============================

  async obtenerProveedores(filtros: ProveedorFilter = {}): Promise<PaginatedResponse<Proveedor>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);

    const response = await ApiClient.get(`/proveedores?${params.toString()}`);
    return response.data;
  }

  async obtenerProveedor(id: number): Promise<Proveedor> {
    const response = await ApiClient.get(`/proveedores/${id}`);
    return response.data;
  }

  async crearProveedor(datos: CrearProveedor): Promise<Proveedor> {
    const response = await ApiClient.post(`/proveedores`, datos);
    return response.data;
  }

  async actualizarProveedor(id: number, datos: Partial<CrearProveedor>): Promise<Proveedor> {
    const response = await ApiClient.put(`/proveedores/${id}`, datos);
    return response.data;
  }

  async eliminarProveedor(id: number): Promise<void> {
    await ApiClient.delete(`/proveedores/${id}`);
  }

  async obtenerListaProveedores(): Promise<Proveedor[]> {
    const response = await ApiClient.get(`/proveedores/lista`);
    return response.data;
  }

  // ===============================
  // VEHICULOS
  // ===============================

  async obtenerVehiculos(filtros: VehiculoFilter = {}): Promise<PaginatedResponse<Vehiculo>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.tipo) params.append('tipo', filtros.tipo);

    const response = await ApiClient.get(`/vehiculos?${params.toString()}`);
    return response.data;
  }

  async obtenerVehiculo(id: number): Promise<Vehiculo> {
    const response = await ApiClient.get(`/vehiculos/${id}`);
    return response.data;
  }

  async crearVehiculo(datos: CrearVehiculo): Promise<Vehiculo> {
    const response = await ApiClient.post(`/vehiculos`, datos);
    return response.data;
  }

  async actualizarVehiculo(id: number, datos: Partial<CrearVehiculo>): Promise<Vehiculo> {
    const response = await ApiClient.put(`/vehiculos/${id}`, datos);
    return response.data;
  }

  async eliminarVehiculo(id: number): Promise<void> {
    await ApiClient.delete(`/vehiculos/${id}`);
  }

  async obtenerListaVehiculos(): Promise<Vehiculo[]> {
    const response = await ApiClient.get(`/vehiculos/lista`);
    return response.data;
  }

  // ===============================
  // TARJETAS
  // ===============================

  async obtenerTarjetas(filtros: TarjetaFilter = {}): Promise<PaginatedResponse<Tarjeta>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);

    const response = await ApiClient.get(`/tarjetas?${params.toString()}`);
    return response.data;
  }

  async crearTarjeta(datos: CrearTarjeta): Promise<Tarjeta> {
    const response = await ApiClient.post(`/tarjetas`, datos);
    return response.data;
  }

  async actualizarTarjeta(id: number, datos: Partial<CrearTarjeta>): Promise<Tarjeta> {
    const response = await ApiClient.put(`/tarjetas/${id}`, datos);
    return response.data;
  }

  async eliminarTarjeta(id: number): Promise<void> {
    await ApiClient.delete(`/tarjetas/${id}`);
  }

  async obtenerListaTarjetas(): Promise<Tarjeta[]> {
    const response = await ApiClient.get(`/tarjetas/lista`);
    return response.data;
  }

  // ===============================
  // EMISORES
  // ===============================

  async obtenerEmisores(filtros: EmisorFilter = {}): Promise<PaginatedResponse<Emisor>> {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page.toString());
    if (filtros.limit) params.append('per_page', filtros.limit.toString());
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.estado) params.append('estado', filtros.estado);

    const response = await ApiClient.get(`/emisores?${params.toString()}`);
    return response.data;
  }

  async crearEmisor(datos: CrearEmisor): Promise<Emisor> {
    const response = await ApiClient.post(`/emisores`, datos);
    return response.data;
  }

  async actualizarEmisor(id: number, datos: Partial<CrearEmisor>): Promise<Emisor> {
    const response = await ApiClient.put(`/emisores/${id}`, datos);
    return response.data;
  }

  async eliminarEmisor(id: number): Promise<void> {
    await ApiClient.delete(`/emisores/${id}`);
  }

  async obtenerListaEmisores(): Promise<Emisor[]> {
    const response = await ApiClient.get(`/emisores/lista`);
    return response.data;
  }

  // ===============================
  // LISTAS AUXILIARES
  // ===============================

  async obtenerListaOperadores(): Promise<Operador[]> {
    const response = await ApiClient.get(`/operadores/lista`);
    return response.data;
  }

  async obtenerListaFaenas(): Promise<Faena[]> {
    const response = await ApiClient.get(`/faenas/lista`);
    return response.data;
  }

  async obtenerListaPredios(): Promise<Predio[]> {
    const response = await ApiClient.get(`/predios/lista`);
    return response.data;
  }

  async obtenerListaInsumos(): Promise<Insumo[]> {
    const response = await ApiClient.get(`/insumos/lista`);
    return response.data;
  }

  async obtenerListaItemsChequeo(): Promise<ItemChequeo[]> {
    const response = await ApiClient.get(`/items-chequeo/lista`);
    return response.data;
  }

  // 🆕 NUEVO: Lista de máquinas para dropdowns
  async obtenerListaMaquinas(): Promise<any[]> { // Usar 'any' temporalmente
    const response = await ApiClient.get(`/maquinas/lista`);
    return response.data;
  }
}

// ===============================
// EXPORTAR SINGLETON E INTERFACES
// ===============================

export default new ReferenciasService();

export type {
  BaseFilter,
  FaenaFilter,
  PredioFilter,
  RodalFilter,
  InsumoFilter,
  ItemChequeoFilter,
  ProveedorFilter,
  VehiculoFilter,
  TarjetaFilter,
  EmisorFilter,
  CrearOperador,
  CrearFaena,
  CrearPredio,
  CrearRodal,
  CrearInsumo,
  CrearItemChequeo,
  CrearProveedor,
  CrearVehiculo,
  CrearTarjeta,
  CrearEmisor
};
